import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CreditCard, Truck, ArrowLeft, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { StoreHeader } from '../components/StoreHeader';
import { StoreFooter } from '../components/StoreFooter';
import { useCartStore } from '../stores/cartStore';
import { useAuthStore } from '../stores/authStore';
import { orderApi, mpesaApi, authApi } from '../lib/api';
import { toast } from 'sonner';
import { useEffect } from "react";

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, clearCart } = useCartStore();
  const totalPrice = useCartStore((state) => state.getTotalPrice());
  const { isAuthenticated, user } = useAuthStore();

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
  });
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [loading, setLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);

  const originalTotal = items.reduce(
    (sum, item) => sum + ((item.original_price ?? item.price) * item.quantity),
    0
  );
  const discountedTotal = items.reduce(
    (sum, item) => sum + (item.price * item.quantity),
    0
  );

  const savings = Math.max(0, originalTotal - discountedTotal);
  const savingsPercent = originalTotal > 0 ? (savings / originalTotal) * 100 : 0;

  // Shipping rule
  const shipping = discountedTotal > 15000 ? 0 : 300;

  // Final total
  const finalTotal = discountedTotal + shipping;
  const tax = 0;
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      toast.error('Please enter a valid email');
      return false;
    }
    if (!formData.phone.trim() || formData.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return false;
    }
    if (!formData.address.trim() || formData.address.length < 10) {
      toast.error('Please enter your delivery address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    try {
      // Create guest account if not authenticated
      tax: savings
      if (!isAuthenticated) {
        await authApi.guestCheckout(formData.email, formData.name, formData.phone);

      }

      // Create order
      const orderData = {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        delivery_address: formData.address,
        items: items.map((item) => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          unit_price: item.price,
          total_price: item.price * item.quantity,
          // Optional fields
          ...(item.size ? { size: item.size } : {}),
          ...(item.color ? { color: item.color } : {}),
        })),
        subtotal: totalPrice,
        tax: savings,
        shipping_cost: shipping,
        total_amount: finalTotal,
        payment_method: paymentMethod,
      };

      const orderRes = await orderApi.create(orderData);
      const order = orderRes.data;

      if (paymentMethod === 'mpesa') {
        // Initiate M-Pesa STK Push
        const mpesaRes = await mpesaApi.initiateStkPush({
          phone_number: formData.phone,
          amount: finalTotal,
          order_id: order.id,
        });

        if (mpesaRes.data.mock) {
          toast.info('M-Pesa is not configured. Order placed with Cash on Delivery.');
        } else if (mpesaRes.data.success) {
          toast.success('Check your phone for the M-Pesa prompt');
        }
      }

      setOrderSuccess(order);
      clearCart();
      toast.success('Order placed successfully!');
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0 && !orderSuccess) {
    navigate('/cart');
    return null;
  }

   useEffect(() => {
      window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

  if (orderSuccess) {
    return (
      <div className="min-h-screen bg-stone-100">
        <StoreHeader />
        <main className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-white border border-stone-200 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-stone-900 mb-2">Order Confirmed!</h1>
            <p className="text-stone-600 mb-6">
              Thank you for your order. We've sent a confirmation to {formData.email}
            </p>
            <div className="bg-stone-50 p-4 mb-6 text-left">
              <p className="text-sm text-stone-500">Order Number</p>
              <p className="font-mono font-bold text-lg text-emerald-900" data-testid="order-number">
                {orderSuccess.order_number}
              </p>
            </div>
            <div className="flex gap-4 justify-center">
              <Link to={`/orders`}>
                <Button variant="outline" data-testid="track-order-btn">
                  Track Order
                </Button>
              </Link>
              <Link to="/products">
                <Button className="bg-emerald-900 hover:bg-emerald-800" data-testid="continue-shopping-success-btn">
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </div>
        </main>
        <StoreFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100">
      <StoreHeader />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Link to="/cart" className="inline-flex items-center text-sm text-stone-600 hover:text-emerald-900 mb-6">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Cart
        </Link>

        <h1 className="text-3xl font-bold text-stone-900 mb-8">Checkout</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Info */}
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="text-lg font-bold text-stone-900 mb-4">Contact Information</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      className="mt-1"
                      data-testid="checkout-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      className="mt-1"
                      data-testid="checkout-email"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="0712345678 or 254712345678"
                      className="mt-1"
                      data-testid="checkout-phone"
                    />
                    <p className="text-xs text-stone-500 mt-1">
                      {paymentMethod === 'mpesa' && 'This number will receive the M-Pesa payment prompt'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="text-lg font-bold text-stone-900 mb-4">Notes and Delivery Address</h2>
                <div>
                  <Label htmlFor="address">Give us any useful or additional information and the Full Delivery Address *</Label>
                  <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Street address, Building, Apartment, City, County"
                    rows={3}
                    className="w-full mt-1 p-3 border border-stone-200 rounded-none focus:ring-2 focus:ring-emerald-900 focus:border-transparent"
                    data-testid="checkout-address"
                  />
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="text-lg font-bold text-stone-900 mb-4">Payment Method</h2>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                  <div className="space-y-3">
                    <label
                      className={`flex items-center gap-4 p-4 border cursor-pointer transition ${paymentMethod === 'mpesa'
                          ? 'border-emerald-900 bg-emerald-50'
                          : 'border-stone-200 hover:border-stone-300'
                        }`}
                      data-testid="payment-mpesa"
                    >
                      <RadioGroupItem value="mpesa" />
                      <div className="w-10 h-10 bg-green-600 rounded flex items-center justify-center text-white font-bold">
                        M
                      </div>
                      <div>
                        <p className="font-semibold">M-Pesa</p>
                        <p className="text-sm text-stone-500">Pay via M-Pesa STK Push</p>
                      </div>
                    </label>

                    <label
                      className={`flex items-center gap-4 p-4 border cursor-pointer transition ${paymentMethod === 'cod'
                          ? 'border-emerald-900 bg-emerald-50'
                          : 'border-stone-200 hover:border-stone-300'
                        }`}
                      data-testid="payment-cod"
                    >
                      <RadioGroupItem value="cod" />
                      <div className="w-10 h-10 bg-stone-700 rounded flex items-center justify-center">
                        <Truck className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold">Cash on Delivery</p>
                        <p className="text-sm text-stone-500">Pay when you receive your order</p>
                      </div>
                    </label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-stone-200 p-6 sticky top-24">
                <h2 className="text-lg font-bold text-stone-900 mb-4">Order Summary</h2>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-stone-600">Original Price</span>
                    <span className="font-medium">KES {originalTotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-600">You Saved</span>
                    <span className="font-medium text-green-600">
                      KES {savings.toLocaleString()} ({savingsPercent.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-600">Shipping</span>
                    <span className="font-medium">
                      {shipping === 0 ? (
                        <span className="text-green-600">FREE</span>
                      ) : (
                        `KES ${shipping.toLocaleString()}`
                      )}
                    </span>
                  </div>

                  {discountedTotal < 5000 && (
                    <p className="text-xs text-stone-500 pt-2">
                      Add KES {(5000 - discountedTotal).toLocaleString()} more for free shipping!
                    </p>
                  )}

                  <div className="border-t border-stone-200 pt-3 mt-3">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span className="text-emerald-900">KES {finalTotal.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full mt-6 bg-emerald-900 hover:bg-emerald-800 uppercase tracking-wider font-bold"
                  disabled={loading}
                  data-testid="place-order-btn"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing...
                    </div>
                  ) : (
                    <>
                      {paymentMethod === 'mpesa' ? 'Pay with M-Pesa' : 'Place Order'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </main>

      <StoreFooter />
    </div>
  );
}
